// Bill detail: one supplier bill, with its lines, payments, and history.
// Single-get shape (`FinanceBillDetailResponse`) plus the bill's payments
// list; status changes go through the transition endpoint, money out through
// the pay endpoint.
//
// NOTE on duplicate-bill warnings: there is deliberately no "possible
// duplicate" banner here. No duplicate signal exists on any bills/invoices
// read path — the only `duplicate_count` in the finance surface counts
// skipped duplicate lines during a bank-statement import, and
// `create_bill` is served by server-assigned BILL- numbers only. Do not add
// a heuristic duplicate warning without a real backend signal.

import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link, useParams } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { PeriodLockWarning } from "@/components/PeriodLockWarning";
import {
  type AuditEvent,
  type BankAccount,
  type BillDetail,
  type BillPayment,
  getBillDetail,
  getPeriodLock,
  listAuditEvents,
  listBankAccounts,
  listBillPayments,
  listChartAccounts,
  payBill,
  transitionBill,
} from "@/lib/api";
import { minorUnitEntry, toMinorUnits } from "@/lib/money";

function statusTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case "paid":
      return "success";
    case "overdue":
      return "danger";
    case "received":
      return "info";
    default:
      return "idle";
  }
}

function plainStatus(status: string): string {
  const lower = status.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function todayInput(): string {
  return new Date().toISOString().slice(0, 10);
}

export function BillDetailPage() {
  const { billId } = useParams({ strict: false }) as { billId: string };
  const [detail, setDetail] = useState<BillDetail | null>(null);
  const [payments, setPayments] = useState<BillPayment[] | null>(null);
  const [events, setEvents] = useState<AuditEvent[] | null>(null);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [accountLabels, setAccountLabels] = useState<Map<string, string>>(
    new Map(),
  );
  const [lockDate, setLockDate] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [payOpen, setPayOpen] = useState(false);
  const [acting, setActing] = useState<"recognize" | "reverse" | null>(null);
  const [confirming, setConfirming] = useState<"recognize" | "reverse" | null>(
    null,
  );

  const reload = useCallback(() => {
    if (!billId) return;
    setError(null);
    getBillDetail(billId)
      .then((loaded) => {
        setDetail(loaded);
        setPayments(loaded.payments);
      })
      .catch((cause: unknown) => setError(toErrorMessage(cause)));
    listBillPayments({ bill_id: billId })
      .then(setPayments)
      .catch(() => {
        // The single-get already carries payments; a list failure here must
        // not blank the Payments tab.
      });
    listAuditEvents({ resource_id: billId })
      .then((rows) => {
        setEvents(rows);
        setEventsError(null);
      })
      .catch((cause: unknown) => {
        setEvents(null);
        setEventsError(toErrorMessage(cause));
      });
    listChartAccounts()
      .then((accounts) =>
        setAccountLabels(
          new Map(
            accounts.map((account) => [
              account.id,
              `${account.code} — ${account.name}`,
            ]),
          ),
        ),
      )
      .catch(() => setAccountLabels(new Map()));
    getPeriodLock()
      .then((lock) => setLockDate(lock.lock_date ?? lock.advisor_lock_date))
      .catch(() => setLockDate(undefined));
  }, [billId]);

  useEffect(reload, [reload]);

  const loadActivity = useCallback(() => {
    if (!billId) return;
    setEventsError(null);
    listAuditEvents({ resource_id: billId })
      .then((rows) => {
        setEvents(rows);
        setEventsError(null);
      })
      .catch((cause: unknown) => {
        setEvents(null);
        setEventsError(toErrorMessage(cause));
      });
  }, [billId]);

  const recognize = async () => {
    if (!billId || !detail?.issue_date) return;
    setActing("recognize");
    setError(null);
    try {
      await transitionBill(billId, "received");
      reload();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setActing(null);
    }
  };

  const reverse = async () => {
    if (!billId || !detail?.issue_date) return;
    setActing("reverse");
    setError(null);
    try {
      await transitionBill(billId, "void");
      reload();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setActing(null);
    }
  };

  const paidCents = [payments, detail?.payments]
    .filter((rows): rows is NonNullable<typeof rows> => rows != null)
    .reduce(
      (highest, rows) =>
        Math.max(
          highest,
          rows.reduce((sum, payment) => sum + payment.amount_cents, 0),
        ),
      0,
    );
  const totalCents = detail?.total_cents ?? 0;
  const currency = detail?.currency_code || "USD";
  const remainingCents = Math.max(0, totalCents - paidCents);
  const status = detail?.status.toLowerCase() ?? "";
  const canRecognize = status === "draft";
  const canPay = status === "received" || status === "overdue";
  const canReverse = canPay && paidCents === 0;
  const recognizeDisabled = acting !== null || !detail?.issue_date;
  const reverseDisabled = acting !== null || !detail?.issue_date;

  return (
    <div className="grid gap-6">
      <nav aria-label="Breadcrumb" className="text-sm">
        <Link
          to="/bills"
          className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:underline"
        >
          Bills
        </Link>
        <span
          aria-hidden="true"
          className="mx-2 text-[var(--muted-foreground)]"
        >
          /
        </span>
        <span aria-current="page" className="font-medium">
          {detail?.bill_number ?? billId}
        </span>
      </nav>

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={reload}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {detail === null && !error ? (
        <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
      ) : detail ? (
        <>
          <PageHeader
            title={detail.bill_number}
            description={
              <>
                {detail.supplier_name} · due{" "}
                {new Date(detail.due_at).toLocaleDateString()} ·{" "}
                <StatusDot
                  tone={statusTone(detail.status)}
                  label={plainStatus(detail.status)}
                />
              </>
            }
            actions={
              <>
                {canRecognize ? (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setConfirming("recognize")}
                    disabled={recognizeDisabled}
                    title={
                      detail.issue_date
                        ? `Posts the expense and payables journal on ${detail.issue_date}`
                        : "Add an issue date before recognizing this bill"
                    }
                  >
                    {acting === "recognize" ? "Recognizing…" : "Recognize"}
                  </Button>
                ) : null}
                {canReverse ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setConfirming("reverse")}
                    disabled={reverseDisabled}
                    title={
                      detail.issue_date
                        ? `Posts a reversing journal on ${detail.issue_date} and marks the bill void`
                        : "This bill has no issue date to reverse against"
                    }
                  >
                    {acting === "reverse" ? "Reversing…" : "Reverse"}
                  </Button>
                ) : null}
                {canPay ? (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setPayOpen(true)}
                  >
                    Pay bill
                  </Button>
                ) : null}
                {status !== "void" && status !== "draft" ? (
                  <Button asChild variant="secondary" size="sm">
                    <Link
                      to="/bills"
                      search={(previous) => ({
                        ...previous,
                        view: "credits",
                        bill: detail.id,
                      })}
                    >
                      Supplier credit
                    </Link>
                  </Button>
                ) : null}
                {status !== "void" ? (
                  <Button asChild variant="secondary" size="sm">
                    <Link
                      to="/bills"
                      search={(previous) => ({
                        ...previous,
                        view: "recurring",
                      })}
                    >
                      Make recurring
                    </Link>
                  </Button>
                ) : null}
                {canPay ? (
                  <Button asChild variant="secondary" size="sm">
                    <Link
                      to="/payments"
                      search={(previous) => ({
                        ...previous,
                        tab: "scheduled",
                        bill: detail.id,
                      })}
                    >
                      Schedule payment
                    </Link>
                  </Button>
                ) : null}
              </>
            }
          />

          {canRecognize && !detail.issue_date ? (
            <p className="text-sm text-[var(--muted-foreground)]">
              Recognize posts the expense and accounts payable journal on the
              bill issue date. This draft has no issue date, so Recognize stays
              unavailable.
            </p>
          ) : null}
          {canReverse && detail.issue_date ? (
            <p className="text-sm text-[var(--muted-foreground)]">
              Reverse posts a reversing journal on {detail.issue_date}, the
              bill issue date, and marks the bill void. It is available only
              while this bill has no payments or applied supplier credits.
            </p>
          ) : null}
          {(status === "received" || status === "overdue") &&
          paidCents > 0 ? (
            <p className="text-sm text-[var(--muted-foreground)]">
              Reverse stays unavailable while payments are recorded. Remove
              those settlements before voiding this bill. Paid status stays on
              Pay bill.
            </p>
          ) : null}
          {detail.recognition_journal_id || detail.reversal_journal_id ? (
            <p className="text-sm">
              {detail.recognition_journal_id ? (
                <Link
                  to="/journal/$entryId"
                  params={{ entryId: detail.recognition_journal_id }}
                  className="underline-offset-4 hover:underline"
                >
                  Recognition journal
                </Link>
              ) : null}
              {detail.recognition_journal_id && detail.reversal_journal_id
                ? " · "
                : null}
              {detail.reversal_journal_id ? (
                <Link
                  to="/journal/$entryId"
                  params={{ entryId: detail.reversal_journal_id }}
                  className="underline-offset-4 hover:underline"
                >
                  Reversal journal
                </Link>
              ) : null}
            </p>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Total
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {formatMoney({ cents: totalCents, currency })}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Paid so far
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {formatMoney({ cents: paidCents, currency })}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Still owed
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {formatMoney({ cents: remainingCents, currency })}
              </p>
            </div>
          </div>

          <section className="grid gap-3">
            <h2 className="text-sm font-medium">Lines</h2>
              <div className="surface overflow-hidden rounded-2xl">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>What</TableHead>
                      <TableHead>Account</TableHead>
                      <TableHead className="text-right">Tax</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detail.lines.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4}>
                          This bill has no lines.
                        </TableCell>
                      </TableRow>
                    ) : (
                      detail.lines.map((line) => (
                        <TableRow key={line.id}>
                          <TableCell>{line.description}</TableCell>
                          <TableCell className="text-xs">
                            {accountLabels.get(line.account_id) ?? (
                              <span
                                className="text-[var(--muted-foreground)]"
                                title={line.account_id}
                              >
                                Unknown account
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {line.tax_cents > 0 ? (
                              formatMoney({
                                cents: line.tax_cents,
                                currency,
                              })
                            ) : (
                              <span className="text-[var(--muted-foreground)]">
                                —
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatMoney({
                              cents: line.amount_minor,
                              currency,
                            })}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            <h2 className="text-sm font-medium">Payments</h2>
              <div className="surface overflow-hidden rounded-2xl">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Paid on</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments === null ? (
                      <TableRow>
                        <TableCell colSpan={4}>Loading…</TableCell>
                      </TableRow>
                    ) : payments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4}>
                          No payments recorded yet — use Pay bill above.
                        </TableCell>
                      </TableRow>
                    ) : (
                      payments.map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell className="whitespace-nowrap">
                            {new Date(payment.received_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>{payment.method}</TableCell>
                          <TableCell>{payment.status}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatMoney({
                              cents: payment.amount_cents,
                              currency,
                            })}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
          </section>

          <details className="surface rounded-2xl p-4">
            <summary className="cursor-pointer text-sm font-medium">
              Activity
            </summary>
              <div className="mt-3 overflow-hidden rounded-xl">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>What happened</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {events === null && !eventsError ? (
                      <TableRow>
                        <TableCell colSpan={2}>Loading…</TableCell>
                      </TableRow>
                    ) : eventsError ? (
                      <TableRow>
                        <TableCell colSpan={2}>
                          <span className="text-[var(--danger)]">
                            Could not load activity: {eventsError}
                          </span>
                          <div className="mt-2">
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={loadActivity}
                            >
                              Retry
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : events && events.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2}>
                          No recorded activity for this bill yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      (events ?? []).map((event) => (
                        <TableRow key={event.id}>
                          <TableCell className="whitespace-nowrap text-xs">
                            {new Date(event.occurred_at).toLocaleString()}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {event.action}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
          </details>
        </>
      ) : null}

      {payOpen && detail ? (
        <PayBillDrawer
          billId={detail.id}
          currencyCode={detail.currency_code}
          lockDate={lockDate}
          onClose={() => setPayOpen(false)}
          onDone={reload}
        />
      ) : null}

      <Dialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
        title={confirming === "recognize" ? "Recognize this bill?" : "Reverse this bill?"}
        description={
          confirming === "recognize"
            ? `Posts the expense and payables journal on ${detail?.issue_date ?? "the bill issue date"}, moving ${detail?.bill_number ?? "this bill"} from draft to received.`
            : `Posts a reversing journal on ${detail?.issue_date ?? "the bill issue date"} and marks ${detail?.bill_number ?? "this bill"} void.`
        }
      >
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setConfirming(null)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              const action = confirming;
              setConfirming(null);
              if (action === "recognize") void recognize();
              if (action === "reverse") void reverse();
            }}
          >
            {confirming === "recognize" ? "Recognize bill" : "Reverse bill"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function PayBillDrawer({
  billId,
  currencyCode,
  lockDate,
  onClose,
  onDone,
}: {
  billId: string;
  currencyCode: string;
  lockDate?: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("bank_transfer");
  const [paidOn, setPaidOn] = useState(todayInput);
  const [bankAccountId, setBankAccountId] = useState("");
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const code = currencyCode || "USD";
  const entry = minorUnitEntry(code);

  useEffect(() => {
    listBankAccounts()
      .then(setBankAccounts)
      .catch(() => setBankAccounts([]));
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const amount_minor = toMinorUnits(amount, code);
      if (amount_minor === null)
        throw new Error("Amount must be a positive number (e.g. 12.50).");
      if (!paidOn) throw new Error("Paid-on date is required.");
      if (!method.trim()) throw new Error("Payment method is required.");
      await payBill(billId, {
        amount_minor,
        method: method.trim(),
        received_at: new Date(`${paidOn}T00:00:00`).toISOString(),
        ...(bankAccountId ? { bank_account_id: bankAccountId } : {}),
      });
      onDone();
      onClose();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      title="Pay this bill"
      description="Bookkeeping only — records the payment you made; it never sends money to a bank."
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <form onSubmit={submit} className="grid gap-3">
        <PeriodLockWarning lockDate={lockDate} chosenDate={paidOn} />
        {error ? (
          <p role="alert" className="text-sm text-[var(--danger)]">
            {error}
          </p>
        ) : null}
        <label
          htmlFor="bill-pay-amount"
          className="grid gap-1 text-xs font-medium"
        >
          Amount ({code})
          <Input
            id="bill-pay-amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            placeholder={entry.placeholder}
            step={entry.step}
            min="0"
            inputMode="decimal"
            disabled={saving}
          />
        </label>
        <label
          htmlFor="bill-pay-method"
          className="grid gap-1 text-xs font-medium"
        >
          Method
          <Input
            id="bill-pay-method"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            required
            placeholder="bank_transfer"
            disabled={saving}
          />
        </label>
        <label
          htmlFor="bill-pay-date"
          className="grid gap-1 text-xs font-medium"
        >
          Paid on
          <Input
            id="bill-pay-date"
            type="date"
            value={paidOn}
            onChange={(e) => setPaidOn(e.target.value)}
            required
            disabled={saving}
          />
        </label>
        <label
          htmlFor="bill-pay-account"
          className="grid gap-1 text-xs font-medium"
        >
          Account
          <select
            id="bill-pay-account"
            value={bankAccountId}
            onChange={(e) => setBankAccountId(e.target.value)}
            disabled={saving}
            className="h-9 rounded-md border border-[var(--border)] bg-[var(--background)] px-2 text-sm"
          >
            <option value="">Default bank account (1100)</option>
            {bankAccounts
              .filter(
                (account) =>
                  (account.is_active ?? account.active ?? true) &&
                  account.account_type !== "loan",
              )
              .map((account) => (
                <option key={account.id} value={account.id}>
                  {account.account_name} (
                  {account.account_type.replaceAll("_", " ")})
                </option>
              ))}
          </select>
        </label>
        <Button type="submit" disabled={saving}>
          {saving ? "Paying…" : "Record payment"}
        </Button>
      </form>
    </Drawer>
  );
}
